import React from 'react';

import { DragDropProvider, Droppable, Draggable } from '../dnd';
import { recursivelyGetChildren, updateNestedNodes } from '../dnd/helpers';
import { Box, Flex, Text } from 'rebass';

import { backgroundColors } from './colors';

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const getContrastingColor = color => {
  const colorHex = color.replace('#', '');
  const r = parseInt(colorHex.substring(0, 2), 16);
  const g = parseInt(colorHex.substring(2, 4), 16);
  const b = parseInt(colorHex.substring(4, 6), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? '#000' : '#fff';
};

const FormElement = React.forwardRef(({ isDragging, color, label, textColor, children, ...props}, ref) => (
  <Box ref={ref} py={1} {...props}>
    <Box
      p={3}
      css={`
        border-radius: 16px;
        box-shadow: ${isDragging ? '0 0 10px rgba(0,0,0,0.12)' : 'none'};
        border: 1px solid ${isDragging ? 'var(--hoveringHighlightBorder)' : 'var(--border)'};
        background: ${backgroundColors[label].background};
        text-align: left;
        min-height: 200px;
      `}
    >
      <h1
        style={{
          margin: 0,
          fontSize: '54px',
          color: textColor,
        }}
      >{capitalize(label)}</h1>
    </Box>
  </Box>
));

const Well = React.forwardRef((props, ref) => (
  <Flex
    flexDirection="column"
    ref={ref}
    p={2}
    css={`
      /* gap: 10px; */
      width: 100%;
      border-radius: 8px;
    `}
    {...props}
  />
));

const colorsList = Object.entries(backgroundColors).reduce((current, [id, { background, textColor }], idx) => ({
  ...current,
  [id]: {
    id,
    label: id,
    parent: 'root',
    index: idx,
    background,
    textColor,
  }
}), {});

const FormView = ({ showDebug, shouldAnimate }) => {
  const [formElements, setFormElements] = React.useState(colorsList);

  const handleDragEnd = ({ id, from, to }) => {
    console.log({ id, from, to });

    setFormElements(n => {
      return updateNestedNodes(n, id, from ,to);
    });
  };

  const handleDragEndOutline = ({ id, from, to }) => {
    setFormElements(n => {
      return updateNestedNodes(n, id.replace('outline-'), {...from, droppableId: 'root'}, {...to, droppableId: 'root'});
    });
  };

  return (
    <Flex
      css={`
        gap: 44px;
      `}
    >
      <Box
        css={`
          width: 200px;
        `}
      />
      <Box
        css={`
          flex: 1;
          width: 100%;
          max-width: 700px;
        `}
      >
        <DragDropProvider onDragEnd={handleDragEnd} showDebug={showDebug} shouldAnimate={shouldAnimate}>
          {() => (
            <Box
              css={`
                width: 100%;
                display: flex; 
                align-items: flex-start;
                margin: 0 auto;
                & > * {
                  width: 50%;
                  margin: 0 20px;
                }
              `}
            >
              <Droppable droppableId="root">
                {(provided, snapshot) => (
                  <Well
                    ref={provided.innerRef}
                    style={{
                      background: snapshot.isDraggingOver ? 'var(--hoveringHighlight)' : undefined,
                    }}
                    {...provided.droppableProps}
                  >
                    {recursivelyGetChildren(formElements, 'root').map(({id, label, background, textColor}, index) => (
                      <Draggable key={id} draggableId={id} index={index}>
                        {(provided, snapshot) => (
                          <FormElement
                            isDragging={snapshot.isDragging}
                            label={label}
                            textColor={textColor}
                            color={background}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          />  
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    </Well>
                )}
              </Droppable>
            </Box>
          )}
          </DragDropProvider>
      </Box>
      <Box
        data-autoscroll="true"
        css={`
          width: 200px;
          overflow: scroll;
          position: sticky;
          top: calc(66px + 64px);
          height: calc(100vh - 200px);
        `}
      >
        <Text as="label">Outline</Text>
        <DragDropProvider onDragEnd={handleDragEndOutline} showDebug={showDebug} shouldAnimate={shouldAnimate}>
          {() => (
            <Box
              mt={2}
              css={`
                background: var(--background-alt);
                padding: 8px;
                border-radius: 16px;
              `}
            >
              <Droppable droppableId="outline">
                {(provided, snapshot) => (
                  <Well
                    ref={provided.innerRef}
                    style={{
                      background: snapshot.isDraggingOver ? 'var(--hoveringHighlight)' : undefined,
                    }}
                    {...provided.droppableProps}
                  >
                    {recursivelyGetChildren(formElements, 'root').map(({id, label, background, textColor}, index) => (
                      <Draggable key={`outline-${id}`} draggableId={`outline-${id}`} index={index}>
                        {(provided, snapshot) => (
                          <Box
                            py={"1px"}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Box
                              bg={id}
                              isDragging={snapshot.isDragging}
                              css={`
                                color: ${textColor};
                                padding: 6px;
                                font-size: 13px;
                                border: 1px solid var(--border);
                                border-radius: 6px;
                                font-weight: bold;
                                background: ${background};
                              `}
                            >
                              <Text>{capitalize(label)}</Text>
                            </Box>  
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    </Well>
                )}
              </Droppable>
            </Box>
          )}
          </DragDropProvider>
      </Box>
    </Flex>
  );
};

export default FormView;
